/* ---------------------------------------------------------------------------
   The frame: title bar and workspace pane.

   Deliberately the same shape as Bridge Watch's — a title bar with the product
   name, the department beneath it, the deployment's state on the right, and a
   numbered workspace pane down the left. Two applications on one deployment that
   looked like two products would make the shared sign-in read as an accident.

   Not the same CODE, though. Bridge Watch's titlebar and navpane take its own
   `State`, which carries a map view and three scenes; these take this
   application's. The styling is shared, which is where the resemblance actually
   comes from — src/styles/tokens.css and app.css, unchanged.
   --------------------------------------------------------------------------- */

import { h, icon, ICONS } from '../core/dom.ts';
import { agency } from '../tenant/agency.ts';
import { actions } from './actions.ts';
import { SCREENS, store, type Screen, type State } from './store.ts';

export const TITLE: Readonly<Record<Screen, string>> = {
  structures: 'Structures',
  rate: 'Rate a structure',
  ratings: 'Ratings',
  method: 'Method',
};

export const NAV_ICON: Readonly<Record<Screen, keyof typeof ICONS>> = {
  structures: 'list',
  rate: 'edit',
  ratings: 'report',
  method: 'book',
};

/** What the deployment's state should say, in words rather than a colour. */
export function backendLabel(s: Readonly<State>): { text: string; ok: boolean } {
  switch (s.backend) {
    case 'live':
      return {
        text: `D1 LIVE · ${s.bridges?.length ?? 0} STRUCTURES · ${s.ratings?.length ?? 0} RATINGS`,
        ok: true,
      };
    case 'behind':
      return { text: 'DATABASE BEHIND ITS CODE', ok: false };
    case 'signed-out':
      return { text: 'NOT SIGNED IN', ok: false };
    case 'unreachable':
      return { text: 'DEPLOYMENT UNREACHABLE', ok: false };
    default:
      return { text: 'CONNECTING', ok: false };
  }
}

export function titlebar(s: Readonly<State>): HTMLElement {
  const state = backendLabel(s);
  const block = agency();

  return h(
    'header',
    { class: 'rt-tb' },
    h(
      'div',
      { class: 'rt-tb__brand' },
      h('span', { class: 'rt-tb__mark' }, icon(ICONS.bridge, { size: 18 })),
      h(
        'span',
        null,
        h('span', { class: 'rt-tb__name', text: 'BRIDGE RATING' }),
        h('span', {
          class: 'rt-tb__sub',
          text: `${block.departmentShort} · ${block.unitShort}`,
        }),
      ),
    ),
    h('span', { class: 'spacer' }),
    h('span', {
      class: ['rt-tb__state', state.ok ? 'rt-tb__state--ok' : 'rt-tb__state--bad'],
      text: state.text,
      ...(s.backendNote ? { title: s.backendNote } : {}),
    }),
    h(
      'span',
      { class: 'rt-tb__who' },
      h('span', { class: 'rt-tb__whoName', text: s.identity ?? 'not identified' }),
      h('span', { class: 'rt-tb__whoRole', text: (s.role ?? 'unknown role').toUpperCase() }),
    ),
  );
}

export function navpane(s: Readonly<State>): HTMLElement {
  return h(
    'nav',
    { class: 'rt-np' },
    h('div', { class: 'rt-np__label', text: 'WORKSPACE' }),
    ...SCREENS.map((screen, i) =>
      h(
        'button',
        {
          type: 'button',
          class: ['rt-np__item', s.screen === screen && 'rt-np__item--on'],
          onclick: () => actions.go(screen),
        },
        icon(ICONS[NAV_ICON[screen]], { size: 15 }),
        h('span', { text: TITLE[screen] }),
        h('span', { class: 'rt-np__num', text: String(i + 1) }),
      ),
    ),
    h('span', { class: 'spacer' }),
    /* The two applications share a deployment and a session, so this is a plain
       link and not a sign-in: whoever is here is already signed in over there. */
    h(
      'a',
      { class: 'rt-np__cross', href: '#/' },
      icon(ICONS.arrowLeft, { size: 14 }),
      h('span', { text: 'Bridge Watch' }),
    ),
    h('div', {
      class: 'rt-np__note',
      text: `${s.profile.name} · profile ${s.profile.version}`,
    }),
  );
}

export function pagehead(s: Readonly<State>, hint: string): HTMLElement {
  return h(
    'div',
    { class: 'rt-ph' },
    h(
      'div',
      null,
      h('div', { class: 'rt-ph__eyebrow', text: TITLE[s.screen].toUpperCase() }),
      h('h1', { class: 'rt-ph__title', text: TITLE[s.screen] }),
    ),
    h('span', { class: 'spacer' }),
    h('p', { class: 'rt-ph__hint', text: hint }),
  );
}

/**
 * The banner for a deployment that cannot serve this application.
 *
 * One node, above every screen, saying which of the four things is wrong and
 * what to do about it. Null where the deployment is well — a banner that is
 * always present is furniture rather than a warning.
 */
export function deploymentBanner(s: Readonly<State>): HTMLElement | null {
  if (s.backend === 'live') return null;

  const say = (title: string, body: string | null, action?: HTMLElement) =>
    h(
      'div',
      { class: 'rt-bnr' },
      h('div', null, h('strong', { text: title }), body === null ? null : h('p', { text: body })),
      h('span', { class: 'spacer' }),
      action ?? null,
    );

  switch (s.backend) {
    case 'unknown':
      return say('Connecting', null);
    case 'signed-out':
      return say(
        'Not signed in',
        null,
        h('a', { class: 'btn btn--primary', href: '#/', text: 'Go to the start page' }),
      );
    case 'behind':
      return say(
        'The database is behind this build',
        s.backendNote ??
          'Run `npm run db:migrate`.',
      );
    default:
      return say(
        'The deployment did not answer',
        s.backendNote ?? 'Check the network and reload.',
        h('button', {
          type: 'button',
          class: 'btn',
          text: 'Try again',
          onclick: () => {
            store.set({ backend: 'unknown' });
            void actions.connect();
          },
        }),
      );
  }
}
